import {
  addMonths,
  format,
  isAfter,
  isBefore,
  startOfDay,
  subMonths,
} from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Pressable,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import theme from 'shared/styles/theme';

import { asHTMLElement, getInitialPickerDate } from './DatePicker.helpers';
import type { DatePickerProps } from './DatePicker.types';
import DatePickerCalendarGrid, {
  getCalendarGridDays,
  getLocalizedWeekdayHeaders,
} from './DatePickerCalendarGrid';
import type { DatePickerDropdownOption } from './DatePickerDropdown';
import DatePickerDropdown from './DatePickerDropdown';

const DEFAULT_LOCALE = 'en-US';
const KEYBOARD_KEY_ESCAPE = 'Escape';
const CHEVRON_LEFT_ICON = '‹';
const CHEVRON_RIGHT_ICON = '›';
const MONTHS_PER_YEAR = 12;
const EARLIEST_DEFAULT_YEAR = 1900;
const FUTURE_YEAR_BUFFER = 10;
const ISO_DATE_FORMAT = 'yyyy-MM-dd';
const POPOVER_CARD_WIDTH = 320;
const POPOVER_VERTICAL_GAP = 8;
const POPOVER_VIEWPORT_MARGIN = 16;

const getLocalizedMonthNames = (locale: string = DEFAULT_LOCALE): string[] => {
  const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'long' });
  return Array.from({ length: MONTHS_PER_YEAR }, (_, monthIndex) => {
    return monthFormatter.format(new Date(2024, monthIndex, 1));
  });
};

const getYearDropdownOptions = (
  minimumDate?: Date,
  maximumDate?: Date,
): DatePickerDropdownOption[] => {
  const startYear = minimumDate
    ? minimumDate.getFullYear()
    : EARLIEST_DEFAULT_YEAR;
  const endYear = maximumDate
    ? maximumDate.getFullYear()
    : new Date().getFullYear() + FUTURE_YEAR_BUFFER;

  return Array.from({ length: endYear - startYear + 1 }, (_, yearOffset) => {
    const calculatedYear = endYear - yearOffset;
    return {
      label: calculatedYear,
      value: calculatedYear,
    };
  });
};

const DatePicker = ({
  value,
  onValueChange,
  onClear,
  onDismiss,
  minimumDate,
  maximumDate,
  isOpen,
  display = 'bottomsheet',
  anchorRef,
}: DatePickerProps) => {
  const { t, i18n } = useTranslation('componentsStrings', {
    keyPrefix: 'datePickerStrings',
  });
  const activeLocale = i18n?.language || DEFAULT_LOCALE;

  const isBottomSheetDisplay = display === 'bottomsheet';
  const isPickerVisible = !isBottomSheetDisplay || Boolean(isOpen);
  const containerRef = useRef<View>(null);

  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  });

  const selectedDateFormattedString =
    value && !isNaN(value.getTime()) ? format(value, ISO_DATE_FORMAT) : '';

  const [previousSelectedDateString, setPreviousSelectedDateString] = useState(
    selectedDateFormattedString,
  );
  const [activeViewDate, setActiveViewDate] = useState<Date>(() => {
    return getInitialPickerDate(value, maximumDate, minimumDate);
  });
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [popoverCoordinates, setPopoverCoordinates] = useState<{
    top: number;
    left: number;
  } | null>(null);

  if (selectedDateFormattedString !== previousSelectedDateString) {
    setPreviousSelectedDateString(selectedDateFormattedString);
    setActiveViewDate(getInitialPickerDate(value, maximumDate, minimumDate));
  }

  const activeViewYear = activeViewDate.getFullYear();
  const activeViewMonth = activeViewDate.getMonth();

  useEffect(() => {
    if (
      !isPickerVisible ||
      !isBottomSheetDisplay ||
      typeof window === 'undefined'
    ) {
      return undefined;
    }

    const updatePopoverPosition = () => {
      const anchorElement = asHTMLElement(anchorRef?.current);
      const containerElement = asHTMLElement(containerRef.current);

      if (!anchorElement) {
        return;
      }

      const anchorRect = anchorElement.getBoundingClientRect();
      const offsetParent = containerElement?.offsetParent as HTMLElement | null;
      const parentRect = offsetParent?.getBoundingClientRect() ?? {
        top: 0,
        left: 0,
      };

      let top =
        anchorRect.bottom -
        parentRect.top +
        (offsetParent?.scrollTop || 0) +
        POPOVER_VERTICAL_GAP;
      let left =
        anchorRect.left - parentRect.left + (offsetParent?.scrollLeft || 0);

      const viewportWidth =
        typeof window !== 'undefined'
          ? window.innerWidth || document.documentElement?.clientWidth || 0
          : 0;
      if (
        viewportWidth > 0 &&
        anchorRect.left + POPOVER_CARD_WIDTH > viewportWidth
      ) {
        const overflow =
          anchorRect.left +
          POPOVER_CARD_WIDTH -
          viewportWidth +
          POPOVER_VIEWPORT_MARGIN;
        left = Math.max(0, left - overflow);
      }

      setPopoverCoordinates({ top, left });
    };

    updatePopoverPosition();
    window.addEventListener?.('resize', updatePopoverPosition);
    window.addEventListener?.('scroll', updatePopoverPosition, true);

    return () => {
      window.removeEventListener?.('resize', updatePopoverPosition);
      window.removeEventListener?.('scroll', updatePopoverPosition, true);
    };
  }, [anchorRef, isBottomSheetDisplay, isPickerVisible]);

  useEffect(() => {
    if (
      !isPickerVisible ||
      !isBottomSheetDisplay ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const clickTarget = event.target as Node | null;
      if (!clickTarget) {
        return;
      }

      const containerElement = asHTMLElement(containerRef.current);
      if (containerElement?.contains(clickTarget)) {
        return;
      }

      const anchorElement = asHTMLElement(anchorRef?.current);
      if (anchorElement?.contains(clickTarget)) {
        return;
      }

      onDismissRef.current?.();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === KEYBOARD_KEY_ESCAPE) {
        setIsMonthDropdownOpen(false);
        setIsYearDropdownOpen(false);
        onDismissRef.current?.();
      }
    };

    document.addEventListener?.('pointerdown', handlePointerDown);
    document.addEventListener?.('keydown', handleKeyDown);

    return () => {
      document.removeEventListener?.('pointerdown', handlePointerDown);
      document.removeEventListener?.('keydown', handleKeyDown);
    };
  }, [anchorRef, isBottomSheetDisplay, isPickerVisible]);

  const localizedMonthNames = getLocalizedMonthNames(activeLocale);
  const localizedWeekdayHeaders = getLocalizedWeekdayHeaders(activeLocale);

  if (isBottomSheetDisplay && !isOpen) {
    return null;
  }

  const yearDropdownOptions = getYearDropdownOptions(minimumDate, maximumDate);
  const monthDropdownOptions = localizedMonthNames.map(
    (localizedMonthName, monthIndex) => ({
      label: localizedMonthName,
      value: monthIndex,
    }),
  );
  const calendarGridDays = getCalendarGridDays(activeViewYear, activeViewMonth);

  const prevMonthLastDate = new Date(activeViewYear, activeViewMonth, 0);
  const isPrevMonthDisabled = Boolean(
    minimumDate && isBefore(prevMonthLastDate, startOfDay(minimumDate)),
  );

  const nextMonthFirstDate = addMonths(
    new Date(activeViewYear, activeViewMonth, 1),
    1,
  );
  const isNextMonthDisabled = Boolean(
    maximumDate && isAfter(nextMonthFirstDate, maximumDate),
  );

  let containerStyle: StyleProp<ViewStyle> = styles.inlineContainer;
  if (isBottomSheetDisplay && popoverCoordinates) {
    containerStyle = [
      styles.popoverPositioned,
      {
        top: popoverCoordinates.top,
        left: popoverCoordinates.left,
      },
    ];
  } else if (isBottomSheetDisplay) {
    containerStyle = styles.popoverAnchor;
  }

  return (
    <View ref={containerRef} style={containerStyle}>
      <AmplitudeMask>
        <View
          style={styles.card}
          role="dialog"
          aria-label={t('dialogAriaLabel')}
        >
          {/* Header */}
          <View style={styles.header}>
            <Pressable
              disabled={isPrevMonthDisabled}
              onPress={() => {
                setActiveViewDate(subMonths(activeViewDate, 1));
              }}
              accessibilityRole="button"
              accessibilityLabel={t('prevMonth')}
              accessibilityState={{ disabled: isPrevMonthDisabled }}
              style={[styles.navButton, isPrevMonthDisabled && styles.disabled]}
            >
              <BodyBaseText style={styles.navButtonText}>
                {CHEVRON_LEFT_ICON}
              </BodyBaseText>
            </Pressable>

            <View style={styles.dropdownGroup}>
              <DatePickerDropdown
                style={styles.monthDropdown}
                label={localizedMonthNames[activeViewMonth]}
                options={monthDropdownOptions}
                selectedValue={activeViewMonth}
                isOpen={isMonthDropdownOpen}
                onToggle={() => {
                  setIsYearDropdownOpen(false);
                  setIsMonthDropdownOpen((wasOpen) => !wasOpen);
                }}
                onSelect={(selectedMonthIndex) => {
                  setActiveViewDate(
                    new Date(activeViewYear, selectedMonthIndex, 1),
                  );
                  setIsMonthDropdownOpen(false);
                }}
                accessibilityLabel={t('selectMonth')}
              />
              <DatePickerDropdown
                style={styles.yearDropdown}
                alignItemsCenter
                label={activeViewYear}
                options={yearDropdownOptions}
                selectedValue={activeViewYear}
                isOpen={isYearDropdownOpen}
                onToggle={() => {
                  setIsMonthDropdownOpen(false);
                  setIsYearDropdownOpen((wasOpen) => !wasOpen);
                }}
                onSelect={(selectedYearValue) => {
                  setActiveViewDate(
                    new Date(selectedYearValue, activeViewMonth, 1),
                  );
                  setIsYearDropdownOpen(false);
                }}
                accessibilityLabel={t('selectYear')}
              />
            </View>

            <Pressable
              disabled={isNextMonthDisabled}
              onPress={() => {
                setActiveViewDate(addMonths(activeViewDate, 1));
              }}
              accessibilityRole="button"
              accessibilityLabel={t('nextMonth')}
              accessibilityState={{ disabled: isNextMonthDisabled }}
              style={[styles.navButton, isNextMonthDisabled && styles.disabled]}
            >
              <BodyBaseText style={styles.navButtonText}>
                {CHEVRON_RIGHT_ICON}
              </BodyBaseText>
            </Pressable>
          </View>

          {/* Calendar Grid */}
          <DatePickerCalendarGrid
            weekdayHeaders={localizedWeekdayHeaders}
            days={calendarGridDays}
            activeViewMonth={activeViewMonth}
            selectedDate={value}
            minimumDate={minimumDate}
            maximumDate={maximumDate}
            locale={activeLocale}
            onSelectDay={(selectedDay) => {
              setIsMonthDropdownOpen(false);
              setIsYearDropdownOpen(false);
              onValueChange(selectedDay);
              if (isBottomSheetDisplay) {
                onDismissRef.current?.();
              }
            }}
          />

          {/* Footer */}
          {onClear ? (
            <View style={styles.footer}>
              <Pressable
                onPress={() => {
                  onClear();
                  setActiveViewDate(
                    getInitialPickerDate(value, maximumDate, minimumDate),
                  );
                  if (isBottomSheetDisplay) {
                    onDismissRef.current?.();
                  }
                }}
                accessibilityRole="button"
                accessibilityLabel={t('clearDate')}
              >
                <BodyBaseText style={styles.clearText}>
                  {t('clear')}
                </BodyBaseText>
              </Pressable>
            </View>
          ) : null}
        </View>
      </AmplitudeMask>
    </View>
  );
};

const styles = StyleSheet.create({
  popoverAnchor: {
    position: 'absolute',
    top: '100%',
    marginTop: theme.spacing.xs,
    left: 0,
    zIndex: 1000,
  },
  popoverPositioned: {
    position: 'absolute',
    zIndex: 1000,
  },
  inlineContainer: { width: '100%' },
  card: {
    backgroundColor: theme.color.background.primary,
    borderWidth: 1,
    borderColor: theme.color.border.focusedInverse,
    borderRadius: 20,
    padding: theme.spacing.m,
    width: POPOVER_CARD_WIDTH,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.m,
    zIndex: 10,
  },
  dropdownGroup: {
    flexDirection: 'row',
    gap: theme.spacing.xs,
    alignItems: 'center',
    zIndex: 10,
  },
  monthDropdown: {
    width: 116,
  },
  yearDropdown: {
    width: 80,
  },
  navButton: { paddingHorizontal: theme.spacing.xs },
  navButtonText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.color.interactive.primary,
  },
  disabled: { opacity: 0.3 },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: theme.spacing.s,
    paddingTop: theme.spacing.xs,
    borderTopWidth: 1,
    borderTopColor: theme.color.border.primarySolid,
  },
  clearText: {
    color: theme.color.interactive.primary,
    fontWeight: '600',
    fontSize: 14,
  },
});

export default DatePicker;
